<template>
    <LandingPageLayout
        v-bind="landingPageSharedData.getLandingPageLayoutProps()"
    >
        <FeaturePageHeroSection
            title="Flexible availability that works the way you do"
            subtitle="Set the exact hours you want for coaching calls, consulting sessions, and paid offers so clients can only book when it truly works for you."
            buttonLabel="Set your availability"
            :buttonUrl="heroRegisterUrl"
        />

        <section
            v-for="(
                availabilitySection, availabilitySectionIndex
            ) in availabilitySections"
            :id="availabilitySection.id"
            :key="availabilitySection.id"
            :class="[
                availabilitySection.backgroundClass,
                getAvailabilitySectionBorderClass(availabilitySectionIndex),
            ]"
        >
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <div class="grid gap-10 lg:grid-cols-2 lg:items-start">
                    <div :class="availabilitySection.contentOrderClass">
                        <FeaturePageSectionHeader
                            :title="availabilitySection.title"
                            :subtitle="availabilitySection.subtitle"
                            subtitleClass="mt-3 text-slate-600"
                        />
                        <p class="mt-4 text-slate-600">
                            {{ availabilitySection.description }}
                        </p>

                        <div class="mt-6 space-y-3">
                            <FeaturePageInfoCard
                                v-for="example in availabilitySection.examples"
                                :key="example"
                                :description="example"
                                containerClass="rounded-xl border border-slate-200 bg-white p-4"
                                descriptionWithoutTitleClass="text-sm text-slate-700"
                            />
                        </div>

                        <div class="mt-7 flex justify-start">
                            <FeaturePagePrimaryButton
                                :href="ctaRegisterUrl"
                                :label="availabilitySection.buttonLabel"
                            />
                        </div>
                    </div>

                    <div :class="availabilitySection.previewOrderClass">
                        <FeaturePageMockPanel
                            :title="availabilitySection.previewTitle"
                        >
                            <div class="space-y-3">
                                <div
                                    v-for="previewCard in availabilitySection.previewCards"
                                    :key="previewCard.title"
                                    class="rounded-xl border border-slate-200 bg-slate-50 p-4"
                                >
                                    <div
                                        class="flex items-center justify-between gap-4"
                                    >
                                        <div>
                                            <div
                                                class="text-sm font-bold text-slate-900"
                                            >
                                                {{ previewCard.title }}
                                            </div>
                                            <div
                                                class="mt-1 text-sm text-slate-600"
                                            >
                                                {{ previewCard.description }}
                                            </div>
                                        </div>
                                        <div
                                            class="rounded-full bg-primary-shade-ligth px-3 py-1 text-xs font-bold text-primary-shade"
                                        >
                                            {{ previewCard.badge }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </FeaturePageMockPanel>
                    </div>
                </div>
            </div>
        </section>
    </LandingPageLayout>
</template>

<script setup lang="ts">
    import { UtmContentEnum } from '@/Shared/Service/Tracking/Utm/UtmEnums'
    import FeaturePageHeroSection from '@/Marketing/LandingPage/Component/FeaturePage/HeroSection.vue'
    import FeaturePageInfoCard from '@/Marketing/LandingPage/Component/FeaturePage/InfoCard.vue'
    import FeaturePageMockPanel from '@/Marketing/LandingPage/Component/FeaturePage/MockPanel.vue'
    import FeaturePagePrimaryButton from '@/Marketing/LandingPage/Component/FeaturePage/PrimaryButton.vue'
    import FeaturePageSectionHeader from '@/Marketing/LandingPage/Component/FeaturePage/SectionHeader.vue'
    import LandingPageLayout from '@/Marketing/LandingPage/Shared/LandingPageLayout.vue'
    import LandingPageSharedData from '@/Marketing/LandingPage/Entity/LandingPageSharedData'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'

    interface LandingPageProps {
        landingPageSharedDataRawData: any
    }

    interface AvailabilityPreviewCard {
        title: string
        description: string
        badge: string
    }

    interface AvailabilitySection {
        id: string
        title: string
        subtitle: string
        description: string
        examples: string[]
        buttonLabel: string
        previewTitle: string
        previewCards: AvailabilityPreviewCard[]
        contentOrderClass: string
        previewOrderClass: string
        backgroundClass: string
    }

    const props = defineProps<LandingPageProps>()

    const landingPageSharedData = LandingPageSharedData.hydrate(
        props.landingPageSharedDataRawData,
    )
    const urlFactory = new UrlFactory()
    const heroRegisterUrl = urlFactory.makeLandingPageRegisterUrl(
        UtmContentEnum.FlexibleAvailabilityHero,
    )
    const ctaRegisterUrl = urlFactory.makeLandingPageRegisterUrl(
        UtmContentEnum.FlexibleAvailabilityCta,
    )

    function getAvailabilitySectionBorderClass(
        availabilitySectionIndex: number,
    ): string {
        const isLastAvailabilitySection =
            availabilitySectionIndex === availabilitySections.length - 1

        if (isLastAvailabilitySection) {
            return ''
        }

        return 'border-b border-slate-200'
    }

    const availabilitySections: AvailabilitySection[] = [
        {
            id: 'weekly-hours',
            title: 'Weekly Hours',
            subtitle: 'Set the rhythm of your week.',
            description:
                "Define the hours you're available for each day of the week. Add multiple time slots per day when you want separate blocks for calls, deep work, or offline work.",
            examples: [
                'A consultant is available Monday-Friday, 9am-12pm and 2pm-6pm.',
                'A coach opens client sessions on Tuesdays 10am-1pm and Thursdays 3pm-8pm.',
                'A solo expert keeps Saturdays 8am-12pm open for paid intensives only.',
            ],
            buttonLabel: 'Set my weekly hours',
            previewTitle: 'Weekly Schedule',
            previewCards: [
                {
                    title: 'Monday to Friday',
                    description: '09:00-12:00 and 14:00-18:00',
                    badge: 'Default',
                },
                {
                    title: 'Saturday',
                    description: '08:00-12:00',
                    badge: 'Optional',
                },
                {
                    title: 'Sunday',
                    description: 'Unavailable',
                    badge: 'Off',
                },
            ],
            contentOrderClass: 'order-1 lg:order-1',
            previewOrderClass: 'order-2 lg:order-2',
            backgroundClass: 'bg-slate-50',
        },
        {
            id: 'date-specific-hours',
            title: 'Date-Specific Hours',
            subtitle: 'Handle time off and special days without hassle.',
            description:
                'Set special hours for specific dates. These override your weekly schedule so you can adjust availability without changing your regular setup, or block entire days in seconds.',
            examples: [
                'Block out December 25th for a holiday.',
                'Add extended hours for a launch week or workshop on March 10th (8am-8pm).',
                'Shorten your workday on April 5th (9am-11am) for a personal appointment.',
            ],
            buttonLabel: 'Add date overrides',
            previewTitle: 'Date Overrides',
            previewCards: [
                {
                    title: 'Dec 25',
                    description: 'Holiday - no slots available',
                    badge: 'Blocked',
                },
                {
                    title: 'Mar 10',
                    description: 'Special event hours: 08:00-20:00',
                    badge: 'Extended',
                },
                {
                    title: 'Apr 5',
                    description: 'Reduced hours: 09:00-11:00',
                    badge: 'Custom',
                },
            ],
            contentOrderClass: 'order-1 lg:order-2',
            previewOrderClass: 'order-2 lg:order-1',
            backgroundClass: 'bg-white',
        },
        {
            id: 'shared-availabilities',
            title: 'Reusable Availabilities',
            subtitle: 'Keep multiple offers aligned to one schedule.',
            description:
                'Availabilities are created independently in TinyCal. Reuse them so the services tied to the same host stay consistent without rebuilding your schedule from scratch each time.',
            examples: [
                'A consultant uses one Standard Weekday Hours schedule for both discovery calls and paid strategy sessions.',
                'A coach creates an Evening Availability schedule for after-work clients across multiple offers.',
                'A practitioner creates one Holiday Schedule and reuses it across all active services.',
            ],
            buttonLabel: 'Reuse availability across my services',
            previewTitle: 'Availability Assignment',
            previewCards: [
                {
                    title: 'Standard Office Hours',
                    description:
                        'Used by discovery calls and strategy sessions',
                    badge: 'Shared',
                },
                {
                    title: 'Weekend Availability',
                    description: 'Used by VIP intensives and workshop bookings',
                    badge: 'Shared',
                },
                {
                    title: 'Holiday Schedule',
                    description:
                        'Applied across all active offers for year-end',
                    badge: 'Shared',
                },
            ],
            contentOrderClass: 'order-1 lg:order-1',
            previewOrderClass: 'order-2 lg:order-2',
            backgroundClass: 'bg-slate-50',
        },
    ]
</script>
